/**
 * Web 行为漏斗看板 adapter（ADM-MTR-S016）：GET /api/v1/metrics/web-funnels 的 snake_case DTO → 页面消费的
 * camelCase 段 / 行，占位对象拆成 { value, pending }，并派生段内比率（分母为 0 或占位 → null，页面显示 —）。
 *
 * 派生口径（dev-docs AD §19.3）：
 * - 到达率 = 本级 count ÷ eligible（成熟且适用的起点）
 * - 步骤转化率 = 本级 count ÷ 上一适用级 count（跳过 eligible 为 0 的不适用级），仅当两级 eligible 相同时给出
 * - 段与段之间不给比例（计数单位不同）
 */

import type {
  WebFunnelCount,
  WebFunnelData,
  WebFunnelDimension,
  WebFunnelDimensionValues,
  WebFunnelLockedOutcomes,
  WebFunnelMetrics,
  WebFunnelPendingStatus,
  WebFunnelSegment,
  WebFunnelUnit,
} from '@novel-admin/shared';
import { isWebFunnelPending } from '@novel-admin/shared';
import { ratio } from './metrics-paywall-conversion-adapter';

export type WebFunnelCellView = { value: number | null; pending: WebFunnelPendingStatus | null };

export type WebFunnelStageView = {
  key: string;
  label: string;
  count: WebFunnelCellView;
  eligible: number | null;
  reachRate: number | null;
  stepRate: number | null;
};

export type WebFunnelMetricsView = {
  starts: WebFunnelCellView;
  matureStarts: number | null;
  stages: WebFunnelStageView[];
  failures: { counts: Record<string, number> } | { pending: WebFunnelPendingStatus } | null;
  curve:
    | { chapterIndex: number; count: number; rate: number | null }[]
    | { pending: WebFunnelPendingStatus }
    | null;
  paywallStartChapterIndex: number | null;
  arrivals: { locked: number; autoUnlock: number } | { pending: WebFunnelPendingStatus } | null;
  outcomes: WebFunnelLockedOutcomes | { pending: WebFunnelPendingStatus } | null;
};

export type WebFunnelRowView = WebFunnelMetricsView & {
  key: string;
  dims: WebFunnelDimensionValues;
};

export type WebFunnelSegmentView = {
  key: string;
  unit: WebFunnelUnit;
  title: string;
  unitLabel: string;
  summary: WebFunnelMetricsView | null;
  rows: WebFunnelRowView[];
  truncated: boolean;
  activeDays: { date: string; activeUsers: WebFunnelCellView }[] | null;
  settlementDays:
    | {
        date: string;
        observedSettled: WebFunnelCellView;
        settled: number;
        gap: WebFunnelCellView;
        gapRatePct: WebFunnelCellView;
      }[]
    | null;
};

export type WebFunnelViewData = {
  app: WebFunnelData['app'];
  analysis: WebFunnelData['analysis'];
  window: { from: string; to: string };
  lastCompleteDay: string;
  coverage: {
    coverageStart: string | null;
    observableStart: string | null;
    sessionReadyStart: string | null;
  };
  groupBy: WebFunnelDimension[];
  segments: WebFunnelSegmentView[];
  options: WebFunnelData['options'];
  /** 书名 / 章节按 id 预建索引：维度标签与筛选候选逐项查找为常数时间 */
  bookTitleById: Record<string, string>;
  chapterById: Record<string, { novelId: string; chapterIndex: number }>;
  generatedAt: string;
};

export const WEB_FUNNEL_UNIT_LABEL: Record<WebFunnelUnit, string> = {
  OPEN: '打开次数',
  SESSION: '会话',
  USER: '用户',
  USER_PRODUCT: '(用户, 商品)',
  USER_CHAPTER: '(用户, 章节)',
  USER_BOOK: '(用户, 书)',
  DAY: '按日',
};

const SEGMENT_TITLE: Record<string, string> = {
  visitors: '新访客转化',
  opens: '打开段',
  landed_users: '用户段',
  reader_books: '按书阅读深度',
  chapter_arrivals: '收费章到达',
  paywall_sessions: '展示段',
  purchases: '支付段',
  guided_users: '主屏引导',
  active_users: '活跃用户',
  new_visitor_retention: '新访客留存',
  settlement: '到账观察与结算核对',
};

const STAGE_LABEL: Record<string, string> = {
  read_content: '读到正文',
  hit_locked: '碰到收费章',
  paywall_view: '看到价格',
  purchase_attempt: '发起支付',
  settled_observed: '到账观察',
  resolved: '落地成功',
  read_first_chapter: '读到首章',
  reach_chapter_k: '读到第 k 章',
  browser_open: '在系统浏览器打开',
  home_screen_open: '从主屏打开',
  home_screen_revisit: '主屏回访',
  d1: 'D1 回访',
  d7: 'D7 回访',
};

function cell(value: WebFunnelCount): WebFunnelCellView {
  return isWebFunnelPending(value)
    ? { value: null, pending: value.status }
    : { value, pending: null };
}

function metricsView(metrics: WebFunnelMetrics): WebFunnelMetricsView {
  const stages = metrics.stages.map((stage, index): WebFunnelStageView => {
    const count = cell(stage.count);
    // 上一适用级：跳过不适用级（eligible 0，如 §3.6 BROWSER 起点的系统浏览器级），都不适用时以成熟起点为基
    const previous = metrics.stages
      .slice(0, index)
      .reverse()
      .find((candidate) => candidate.eligible !== 0);
    const previousCount = !previous
      ? metrics.mature_starts
      : isWebFunnelPending(previous.count)
        ? null
        : previous.count;
    const previousBase = previous ? previous.eligible : metrics.mature_starts;
    // 同一批起点且分母为正才给步骤率：只经 trigger 归入的组 eligible 为 0，不展示比率
    const sameBase = (stage.eligible ?? 0) > 0 && previousBase === stage.eligible;
    return {
      key: stage.key,
      label: STAGE_LABEL[stage.key] ?? stage.key,
      count,
      eligible: stage.eligible,
      reachRate: ratio(count.value, stage.eligible),
      stepRate: sameBase ? ratio(count.value, previousCount) : null,
    };
  });
  const curve = metrics.curve;
  const outcomes = metrics.outcomes;
  return {
    starts: cell(metrics.starts),
    matureStarts: metrics.mature_starts,
    stages,
    failures:
      metrics.failures === undefined
        ? null
        : isWebFunnelPending(metrics.failures)
          ? { pending: metrics.failures.status }
          : { counts: metrics.failures },
    curve:
      curve === undefined
        ? null
        : isWebFunnelPending(curve)
          ? { pending: curve.status }
          : curve.map((point) => ({
              chapterIndex: point.chapter_index,
              count: point.count,
              rate: ratio(point.count, metrics.mature_starts),
            })),
    paywallStartChapterIndex: metrics.paywall_start_chapter_index ?? null,
    arrivals:
      metrics.arrivals === undefined
        ? null
        : isWebFunnelPending(metrics.arrivals)
          ? { pending: metrics.arrivals.status }
          : { locked: metrics.arrivals.locked, autoUnlock: metrics.arrivals.auto_unlock },
    outcomes:
      outcomes === undefined
        ? null
        : isWebFunnelPending(outcomes)
          ? { pending: outcomes.status }
          : outcomes,
  };
}

function segmentView(
  segment: WebFunnelSegment,
  groupBy: WebFunnelDimension[],
): WebFunnelSegmentView {
  const days = segment.days ?? null;
  const isSettlement = segment.key === 'settlement';
  return {
    key: segment.key,
    unit: segment.unit,
    title: SEGMENT_TITLE[segment.key] ?? segment.key,
    unitLabel: WEB_FUNNEL_UNIT_LABEL[segment.unit],
    summary: segment.summary ? metricsView(segment.summary) : null,
    rows: (segment.rows ?? []).map((row) => ({
      // 维度值是自由文本，整组 JSON 序列化才能保证 key 唯一且 null 不与字面值相撞
      key: JSON.stringify(groupBy.map((dimension) => row.dims[dimension] ?? null)),
      dims: row.dims,
      ...metricsView(row),
    })),
    truncated: segment.truncated ?? false,
    activeDays:
      days && !isSettlement
        ? days.map((day) => ({
            date: day.date,
            activeUsers: cell((day as { active_users: WebFunnelCount }).active_users),
          }))
        : null,
    settlementDays:
      days && isSettlement
        ? days.map((raw) => {
            const day = raw as {
              date: string;
              observed_settled: WebFunnelCount;
              settled: number;
              gap: WebFunnelCount;
              gap_rate_pct: number | null | { status: WebFunnelPendingStatus };
            };
            return {
              date: day.date,
              observedSettled: cell(day.observed_settled),
              settled: day.settled,
              gap: cell(day.gap),
              gapRatePct:
                day.gap_rate_pct === null ? { value: null, pending: null } : cell(day.gap_rate_pct),
            };
          })
        : null,
  };
}

export function adaptMetricsWebFunnels(dto: WebFunnelData): WebFunnelViewData {
  return {
    app: dto.app,
    analysis: dto.analysis,
    window: { from: dto.window.from, to: dto.window.to },
    lastCompleteDay: dto.last_complete_day,
    coverage: {
      coverageStart: dto.coverage.coverage_start,
      observableStart: dto.coverage.observable_start,
      sessionReadyStart: dto.coverage.session_ready_start,
    },
    groupBy: dto.group_by,
    segments: dto.segments.map((segment) => segmentView(segment, dto.group_by)),
    options: dto.options,
    bookTitleById: Object.fromEntries(dto.options.books.map((book) => [book.id, book.title])),
    chapterById: Object.fromEntries(
      dto.options.chapters.map((chapter) => [
        chapter.id,
        { novelId: chapter.novel_id, chapterIndex: chapter.chapter_index },
      ]),
    ),
    generatedAt: dto.generated_at,
  };
}
